import {
  LOAD_CART,
  ADD_PRODUCT,
  REMOVE_PRODUCT,
  DELIVERY_DATE,
  DELIVERY_TIME_SLOT,
  // Phase 2 actions
  LOAD_PHASE2_CART,
  ADD_PHASE2_PRODUCT,
  REMOVE_PHASE2_PRODUCT,
  UPDATE_PHASE2_CART,
  CLEAR_PHASE2_CART,
  // Error actions
  CART_STORE_LIMITATION_ERROR,
  CLEAR_CART_ERROR,
} from "./actionTypes";
import { UPDATE_CART_ITEMS } from "../../configs";
import Axios from "axios";

// Debouncing mechanism for API calls
let phase1ApiCallTimeout = null;
let phase2ApiCallTimeout = null;
const API_DEBOUNCE_DELAY = 300; // 300ms delay

// Utility function to handle cart API calls
const makeCartApiCall = async (payload, userToken) => {
  try {
    // console.log('🚀 Making cart API call:', {
    //   url: UPDATE_CART_ITEMS,
    //   payload,
    //   hasToken: !!userToken
    // });
    
    const response = await Axios.post(UPDATE_CART_ITEMS, payload, {
      headers: {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      }
    });
    
    // console.log('✅ Cart API response:', response.data);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Cart API error:', error.response && error.response.data ? error.response.data : error.message);
    
    return {
      success: false,
      error: error.response && error.response.data ? error.response.data : { message: 'Network error occurred' }
    };
  }
};

// Utility function to handle cart success/error responses
const handleCartResponse = (result, cartType) => {
  if (result.success) {
    const { data } = result;
    // console.log(`✅ ${cartType} Cart Success:`, data.message);
    // console.log(`📦 Items count: ${data.items_count}`);
    // console.log(`🛒 Cart type: ${data.cart_type}`);
    
    // Show success notification if needed
    if (data.items_count > 0) {
      // console.log(`🎉 ${cartType} cart updated successfully with ${data.items_count} items`);
    }
    
    return data;
  } else {
    const { error } = result;
    console.error(`❌ ${cartType} Cart Error:`, error);
    
    if (error.error && error.message) {
      // Handle Sprint cart store limitation error
      if (error.error === "Sprint cart store limitation") {
        console.warn('🚫 Sprint cart store limitation detected:', error.message);
        // Don't show alert here, let the component handle it
        return { shouldHandleError: true, error };
      } else if (error.message === "Cart data is required") {
        // Handle empty cart data error gracefully
        console.warn('🛒 Cart data is required - treating as empty cart');
        return { 
          success: true, 
          data: { 
            message: 'Cart is empty', 
            items_count: 0, 
            cart_type: cartType.toLowerCase() 
          } 
        };
      } else {
        // Show other errors immediately
        console.error('💥 Cart error:', error.message);
        // You can add a toast notification here instead of alert
        // showToast(`❌ ${error.message}`, 'error');
      }
    }
    
    throw new Error(error.message || 'Cart operation failed');
  }
};

// Debounced API call for Phase 2 cart
const debouncedUpdatePhase2Cart = (dispatch, getState) => {
  // Clear any existing timeout
  if (phase2ApiCallTimeout) {
    clearTimeout(phase2ApiCallTimeout);
  }
  
  // Set new timeout
  phase2ApiCallTimeout = setTimeout(() => {
    console.log('🚀 Making debounced Phase 2 cart API call...');
    dispatch(updatePhase2CartProductInternal());
    phase2ApiCallTimeout = null;
  }, API_DEBOUNCE_DELAY);
};

// Debounced API call for Phase 1 cart
const debouncedUpdatePhase1Cart = (dispatch, getState) => {
  // Clear any existing timeout
  if (phase1ApiCallTimeout) {
    clearTimeout(phase1ApiCallTimeout);
  }
  
  // Set new timeout
  phase1ApiCallTimeout = setTimeout(() => {
    console.log('🚀 Making debounced Phase 1 cart API call...');
    dispatch(updateCartProductInternal());
    phase1ApiCallTimeout = null;
  }, API_DEBOUNCE_DELAY);
};

export const loadCart = (products) => ({
  type: LOAD_CART,
  payload: products,
});

export const addProduct = (product) => ({
  type: ADD_PRODUCT,
  payload: product,
});

export const addItemToCart = (product) => (dispatch) => {
  return dispatch({ type: ADD_PRODUCT, payload: product });
};

export const removeProduct = (product) => (dispatch) => {
  return dispatch({ type: REMOVE_PRODUCT, payload: product });
};

// Phase 2 Cart Actions
export const loadPhase2Cart = (products) => ({
  type: LOAD_PHASE2_CART,
  payload: products,
});

export const addPhase2Product = (product) => (dispatch, getState) => {
  // console.log('🛒 addPhase2Product called with:', product);
  
  // Get current Phase 2 cart products
  const currentPhase2Products = getState().cart.phase2Products || [];
  // console.log('🛒 Current Phase 2 cart products:', currentPhase2Products);
  
  // Check if product already exists in cart
  let productAlreadyInCart = false;
  const updatedProducts = [...currentPhase2Products];
  
  updatedProducts.forEach((cp) => {
    if (cp.id === product.id) {
      if (
        JSON.stringify(cp.selectedaddons || []) ===
        JSON.stringify(product.selectedaddons || [])
      ) {
        cp.quantity += 1;
        productAlreadyInCart = true;
        // console.log('🛒 Product already in cart, increased quantity to:', cp.quantity);
      }
    }
  });

  // If product not in cart, add it
  if (!productAlreadyInCart) {
    const newProduct = {
      ...product,
      quantity: 1,
      cart_type: 'phase2',
      selectedaddons: product.selectedaddons || []
    };
    updatedProducts.push(newProduct);
    // console.log('🛒 Added new product to cart:', newProduct);
  }

  // console.log('🛒 Updated cart products:', updatedProducts);

  // Update localStorage
  localStorage.setItem('savedCartItems_relay', JSON.stringify(updatedProducts));
  // console.log('🛒 Updated localStorage with key: savedCartItems_relay');
  
  // Dispatch action to update cart state
  dispatch({
    type: UPDATE_PHASE2_CART,
    payload: updatedProducts,
  });
  // console.log('🛒 Dispatched UPDATE_PHASE2_CART action');

  // Use debounced API call to prevent multiple calls
  debouncedUpdatePhase2Cart(dispatch, getState);
  
  return Promise.resolve(updatedProducts);
};

export const removePhase2Product = (product) => (dispatch, getState) => {
  // Get current Phase 2 cart products
  const currentPhase2Products = getState().cart.phase2Products || [];
  const updatedProducts = [...currentPhase2Products];
  
  const index = updatedProducts.findIndex((p) => p.id === product.id);
  if (index >= 0) {
    updatedProducts.forEach((cp) => {
      if (cp.id === product.id) {
        if (cp.quantity === 1) {
          // Remove product from cart if quantity is 1
          const itemIndex = updatedProducts.findIndex(item => item.id === product.id);
          if (itemIndex >= 0) {
            updatedProducts.splice(itemIndex, 1);
          }
        } else {
          // Decrease quantity by 1
          cp.quantity -= 1;
        }
      }
    });

    // Update localStorage
    localStorage.setItem('savedCartItems_relay', JSON.stringify(updatedProducts));
    
    // Dispatch action to update cart state
    dispatch({
      type: UPDATE_PHASE2_CART,
      payload: updatedProducts,
    });

    // Use debounced API call to prevent multiple calls
    debouncedUpdatePhase2Cart(dispatch, getState);
  }
  
  return Promise.resolve(updatedProducts);
};

export const updatePhase2Cart = (products) => ({
  type: UPDATE_PHASE2_CART,
  payload: products,
});

export const clearPhase2Cart = () => ({
  type: CLEAR_PHASE2_CART,
});

// Internal function for Phase 2 cart API call (not debounced)
const updatePhase2CartProductInternal = () => (dispatch, getState) => {
  let cartItems = [];
  const stateData = getState().cart.phase2Products || [];
  
  if (stateData.length) {
    stateData.map((eachItem) => {
      // Validate required fields before adding to cart
      if (eachItem.id && eachItem.restaurant_id && eachItem.name && eachItem.price && eachItem.quantity) {
        cartItems.push({
          id: eachItem.id,
          restaurant_id: eachItem.restaurant_id,
          item_category_id: eachItem.item_category_id,
          name: eachItem.name,
          price: eachItem.price,
          old_price: eachItem.old_price,
          quantity: eachItem.quantity,
          zone_id: eachItem.zone_id || null,
        });
      } else {
        console.warn('🚫 Skipping invalid Phase 2 cart item (missing required fields):', eachItem);
      }
    });
  }

  const user = getState().user.user;

  if (user && user.data) {
    // Always make API call to sync cart state with server (even when empty)
    const payload = {
      cart_type: "relay", // Phase 2 is Relay
      cart: cartItems, // Send even if empty to properly clear server cart
    };

    return makeCartApiCall(payload, user.data.auth_token)
      .then((result) => {
        return handleCartResponse(result, 'Relay');
      })
      .catch((error) => {
        console.error("Phase 2 (Relay) cart update failed:", error);
        throw error;
      });
  }
};

// Public function for Phase 2 cart update (with debouncing)
export const updatePhase2CartProduct = () => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    // Clear any existing timeout
    if (phase2ApiCallTimeout) {
      clearTimeout(phase2ApiCallTimeout);
    }
    
    // Set new timeout
    phase2ApiCallTimeout = setTimeout(() => {
      console.log('🚀 Making debounced Phase 2 cart API call...');
      
      // Call the internal function that returns a Promise
      const result = dispatch(updatePhase2CartProductInternal());
      
      if (result && typeof result.then === 'function') {
        // If it returns a Promise, handle it
        result
          .then((response) => {
            phase2ApiCallTimeout = null;
            resolve(response);
          })
          .catch((error) => {
            phase2ApiCallTimeout = null;
            reject(error);
          });
      } else {
        // If no Promise returned (e.g., no user), resolve immediately
        phase2ApiCallTimeout = null;
        resolve({ success: true, message: 'Cart updated locally' });
      }
    }, API_DEBOUNCE_DELAY);
  });
};

// Internal function for Phase 1 cart API call (not debounced)
const updateCartProductInternal = () => (dispatch, getState) => {
  let cartItems = [];
  const stateData = getState().cart.products;
  
  if (stateData.length) {
    stateData.map((eachItem) => {
      // Validate required fields before adding to cart
      if (eachItem.id && eachItem.restaurant_id && eachItem.name && eachItem.price && eachItem.quantity) {
      cartItems.push({
        id: eachItem.id,
        restaurant_id: eachItem.restaurant_id,
        item_category_id: eachItem.item_category_id,
        name: eachItem.name,
        price: eachItem.price,
        old_price: eachItem.old_price,
        quantity: eachItem.quantity,
          zone_id: eachItem.zone_id || null,
      });
      } else {
        console.warn('🚫 Skipping invalid Phase 1 cart item (missing required fields):', eachItem);
      }
    });
  }

  const user = getState().user.user;

  if (user && user.data) {
    // Always make API call to sync cart state with server (even when empty)
    const payload = {
      cart_type: "sprint", // Phase 1 is Sprint
      cart: cartItems, // Send even if empty to properly clear server cart
    };

    return makeCartApiCall(payload, user.data.auth_token)
      .then((result) => {
        return handleCartResponse(result, 'Sprint');
      })
      .catch((error) => {
        console.error("Phase 1 (Sprint) cart update failed:", error);
        
        // Handle Sprint cart store limitation error specifically
        if (error.response && error.response.data && error.response.data.error === "Sprint cart store limitation") {
          // Dispatch an action to handle this error in the UI
          dispatch({
            type: CART_STORE_LIMITATION_ERROR,
            payload: {
              error: error.response.data.error,
              message: error.response.data.message,
              cartItems: cartItems
            }
          });
        }
        
        throw error;
      });
  }
};

// Public function for Phase 1 cart update (with debouncing)
export const updateCartProduct = () => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    // Clear any existing timeout
    if (phase1ApiCallTimeout) {
      clearTimeout(phase1ApiCallTimeout);
    }
    
    // Set new timeout
    phase1ApiCallTimeout = setTimeout(() => {
      console.log('🚀 Making debounced Phase 1 cart API call...');
      
      // Call the internal function that returns a Promise
      const result = dispatch(updateCartProductInternal());
      
      if (result && typeof result.then === 'function') {
        // If it returns a Promise, handle it
        result
          .then((response) => {
            phase1ApiCallTimeout = null;
            resolve(response);
          })
          .catch((error) => {
            phase1ApiCallTimeout = null;
            reject(error);
          });
      } else {
        // If no Promise returned (e.g., no user), resolve immediately
        phase1ApiCallTimeout = null;
        resolve({ success: true, message: 'Cart updated locally' });
      }
    }, API_DEBOUNCE_DELAY);
  });
};

export const setDeliveryDate = (date) => (dispatch) => {
  return dispatch({ type: DELIVERY_DATE, payload: date });
};

export const setDeliveryTimeSlot = (slot) => (dispatch) => {
  return dispatch({ type: DELIVERY_TIME_SLOT, payload: slot });
};

// Clear cart errors
export const clearCartError = () => ({
  type: CLEAR_CART_ERROR,
});

// Clear Sprint cart and add new item (for store limitation handling)
export const clearSprintCartAndAddItem = (newItem) => (dispatch, getState) => {
  const user = getState().user.user;
  
  if (user && user.data) {
    // First clear the cart
    const clearPayload = {
      cart_type: "sprint",
      cart: [],
    };
    
    return makeCartApiCall(clearPayload, user.data.auth_token)
      .then((clearResult) => {
        if (clearResult.success) {
          // Clear local cart
          dispatch({ type: LOAD_CART, payload: [] });
          
          // Add new item
          const newCartPayload = {
            cart_type: "sprint",
            cart: [{
              id: newItem.id,
              restaurant_id: newItem.restaurant_id,
              item_category_id: newItem.item_category_id,
              name: newItem.name,
              price: newItem.price,
              old_price: newItem.old_price,
              quantity: newItem.quantity || 1,
              zone_id: newItem.zone_id || null,
            }],
          };
          
          return makeCartApiCall(newCartPayload, user.data.auth_token)
            .then((addResult) => {
              if (addResult.success) {
                dispatch({ type: ADD_PRODUCT, payload: newItem });
                dispatch(clearCartError());
                return addResult.data;
              }
              throw new Error('Failed to add item to cart');
            });
        }
        throw new Error('Failed to clear cart');
      })
      .catch((error) => {
        console.error('Failed to clear cart and add item:', error);
        throw error;
      });
  }
  
  return Promise.reject(new Error('User not authenticated'));
}; 